import { useEffect, useRef } from "react";
import { gsap, motionOK } from "../hooks/useSmoothScroll";
import { useLang } from "../hooks/useLanguage";
import { Reveal } from "./Reveal";

const Benefits = () => {
  const ref = useRef(null);
  const { t } = useLang();

  useEffect(() => {
    if (!motionOK()) return undefined;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".benefit-img",
        { yPercent: -9 },
        {
          yPercent: 9,
          ease: "none",
          scrollTrigger: { trigger: ref.current, start: "top bottom", end: "bottom top", scrub: true },
        }
      );
    }, ref);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={ref} id="benefits" className="bg-ivory text-[#202020] py-24 md:py-32" data-testid="benefits-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <div className="relative overflow-hidden aspect-[3/4] max-w-md">
              <img
                src="/images/terrace.webp"
                alt={t.UI.benefitsImageAlt}
                className="benefit-img absolute inset-0 h-full w-full object-cover scale-[1.2]"
                style={{ objectPosition: "68% center" }}
                loading="lazy"
              />
            </div>
            <Reveal className="mt-6 max-w-md">
              <p className="label-caps">{t.UI.flexibleLabel}</p>
              <p className="mt-3 font-serif-lux text-2xl font-light leading-snug">{t.UI.flexibleText}</p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal>
              <p className="label-caps">{t.UI.benefitsLabel}</p>
              <h2 className="mt-4 font-serif-lux font-light text-[clamp(2rem,4.5vw,3.4rem)] leading-[1.08] tracking-tight">
                {t.UI.benefitsTitle}
              </h2>
              <p className="mt-5 text-smoke max-w-xl leading-relaxed">{t.UI.benefitsBody}</p>
            </Reveal>
            <div className="mt-12 grid sm:grid-cols-2 gap-px bg-[#141414]/10 border hairline-light">
              {t.BENEFITS.map((b, i) => (
                <Reveal key={b.title} delay={i * 0.07}>
                  <div
                    className="group h-full bg-ivory p-8 transition-[background-color,transform] duration-500 hover:bg-white hover:-translate-y-1"
                    data-testid={`benefit-card-${i + 1}`}
                  >
                    <span className="block h-px w-8 bg-champagne mb-6 transition-[width] duration-500 group-hover:w-14" />
                    <h3 className="font-serif-lux text-xl sm:text-2xl font-medium leading-snug">{b.title}</h3>
                    <p className="mt-3 text-sm text-smoke leading-relaxed">{b.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Benefits;
